import { t } from '@lingui/core/macro'
import { useCallback, useMemo, useState } from 'react'
import ReactSelect from 'react-select'
import * as components from '~/components/react_multi_select/components'
import buildStyles from '~/components/react_multi_select/styles'

const selectAllSentinelValue = '<SELECT_ALL>'

function mapSelectedOptions(options, selected) {
  if (selected) {
    return options.filter((option) => selected.includes(option.value))
  } else {
    return []
  }
}

export default function ReactMultiSelect({
  id,
  name,
  options,
  selected,
  onChange,
  isLoading,
  classNamePrefix,
  size,
  hideSelectAll = false,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [searchInputValue, setSearchInputValue] = useState('')
  const selectAllOption = useMemo(
    () => ({
      value: selectAllSentinelValue,
      label: t`Select All`,
    }),
    [],
  )

  // react-select expects values to have the same format as options (with "label" and "value" keys)
  // so here we map the selected values back to their respective options
  // this is necessary to simplify the use of the "onChange" hook
  const value = useMemo(() => mapSelectedOptions(options, selected), [options, selected])

  const isSelectAllSelected = useMemo(() => {
    const optionsNotEmpty = options.length > 0
    const allSelected = value.length === options.length

    return optionsNotEmpty && allSelected
  }, [value, options])

  const isOptionSelected = useCallback(
    (option) => {
      return isSelectAllSelected || value.some((value) => value.value === option.value)
    },
    [value, isSelectAllSelected],
  )

  const optionsWithSelectAll = useMemo(() => {
    if (options.length === 0) {
      return []
    }
    if (hideSelectAll) {
      return options
    }
    return [selectAllOption, ...options]
  }, [options, selectAllOption, hideSelectAll])

  const handleSelectAll = useCallback(
    (newValue, actionMeta) => {
      const { action, option } = actionMeta
      let newValues

      // when the "select all" option is selected
      if (action === 'select-option' && option.value === selectAllSentinelValue) {
        newValues = options
        // when the "select all" option is deselected
      } else if (action === 'deselect-option' && option.value === selectAllSentinelValue) {
        newValues = []
        // regular option
      } else {
        newValues = newValue
      }

      onChange(
        name,
        newValues.map((opt) => opt.value),
      )
    },
    [name, onChange, options],
  )

  const onMenuOpen = useCallback(() => {
    setIsMenuOpen(true)
    setSearchInputValue('')
  }, [])

  const onMenuClose = useCallback(() => {
    setIsMenuOpen(false)
  }, [])

  const onSearchInputFocus = useCallback(() => {
    setIsMenuOpen(true) // keep the menu open as the default behavior would be to close it
  }, [])

  const onSearchInputChange = useCallback((e) => {
    setSearchInputValue(e.currentTarget.value)
  }, [])

  const onSearchInputBlur = useCallback((_e) => {
    // search input loses focus only when a click happens outside the dropdown menu
    // in this case we can close the menu
    setIsMenuOpen(false)
  }, [])

  const onSearchInputMouseDown = useCallback((e) => {
    // prevent event propagation to the parent, which would change focus to the hidden input
    e.stopPropagation()
    e.currentTarget.focus()
  }, [])

  const onMenuBlur = useCallback((_e) => {
    setIsMenuOpen(false) // close the menu when it loses focus due to a click outside of it
  }, [])

  const onMenuMouseDown = useCallback((e) => {
    // keep the focus on the search input instead of switching it to the hidden input
    e.stopPropagation()
    e.preventDefault()
  }, [])

  return (
    <ReactSelect
      classNamePrefix={classNamePrefix}
      backspaceRemovesValue={false} // don't remove values with the backspace key
      blurInputOnSelect={false} // don't remove focus from the input when an option is selected
      closeMenuOnSelect={false} // don't close the dropdown menu when an option is selected
      hideSelectedOptions={false} // don't hide selected options from the list of available options in the menu
      inputId={id}
      inputValue={searchInputValue} // filter options that match the search input value
      isLoading={isLoading}
      isMulti // allow multiple options to be selected
      isOptionSelected={isOptionSelected} // override the built-in logic to detect whether an option is selected or not
      isSearchable={false} // disable search functionality because we implement our own
      menuIsOpen={isMenuOpen} // override the built-int logic used to open or close the menu
      openMenuOnFocus={false} // don't open the dropdown menu when the input is focused
      options={optionsWithSelectAll}
      value={value} // selected options
      // hooks
      onChange={handleSelectAll}
      onMenuClose={onMenuClose}
      onMenuOpen={onMenuOpen}
      // custom props
      isSelectAllSelected={isSelectAllSelected}
      onMenuBlur={onMenuBlur}
      onMenuMouseDown={onMenuMouseDown}
      onSearchInputBlur={onSearchInputBlur}
      onSearchInputChange={onSearchInputChange}
      onSearchInputFocus={onSearchInputFocus}
      onSearchInputMouseDown={onSearchInputMouseDown}
      // customizations
      components={components}
      styles={buildStyles(size)}
    />
  )
}
